import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useUserData } from "./useUserData";
import { updateUserCustomizations } from "../lib/database";

export function useUserCustomizations() {
  const { user } = useAuth();
  const { userData, isLoading: isUserDataLoading, isFetched } = useUserData();
  const queryClient = useQueryClient();

  const customizations = userData?.customizations || {};

  const updateCustomizationsMutation = useMutation({
    mutationFn: async (newCustomizations: Record<string, unknown>) => {
      if (!user?.id) throw new Error("User not authenticated");
      return updateUserCustomizations(user.id, newCustomizations);
    },
    onSuccess: () => {
      // Invalidate user data to refetch with new customizations
      queryClient.invalidateQueries({ queryKey: ["userData", user?.id] });
    },
  });

  const setCustomization = async (key: string, value: unknown) => {
    const newCustomizations = {
      ...customizations,
      [key]: value,
    };
    await updateCustomizationsMutation.mutateAsync(newCustomizations);
  };

  const getCustomization = <T = unknown,>(key: string, defaultValue: T | null = null): T | null => {
    return (customizations[key] as T) ?? defaultValue;
  };

  return {
    customizations,
    setCustomization,
    getCustomization,
    isLoading: isUserDataLoading,
    isFetched,
    isUpdating: updateCustomizationsMutation.isPending,
  };
}
